// PWA install affordance. The browser fires `beforeinstallprompt` when the app
// is installable; we capture and defer it so the app can offer a first-class
// "Install" button instead of relying on the browser's hidden address-bar icon.
// Nothing here touches the network or the document — it only gates a button.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = (): void => { for (const listener of listeners) listener(); };

/** Wire the capture listeners once, before React renders. */
export function initInstallCapture(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** True when the running context is the installed standalone app. */
export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function isInstallAvailable(): boolean {
  return deferred !== null;
}

/** Show the browser's real install prompt; returns the user's choice. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const event = deferred;
  await event.prompt();
  const choice = await event.userChoice;
  deferred = null;
  notify();
  return choice.outcome;
}

export function subscribeInstallable(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}
